import React, { useState, useEffect, useRef } from "react";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Cancel, CheckCircle, Download } from "@mui/icons-material";

import { useAuth } from "../../hooks/AuthProvider";
import TableSearchInput from "../TableSearchInput";
import ButtonTwo from "../../common/dashboard-button";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import AppDropdown from "../../common/app-dropdown";
import { ColorsConstants, searchBarWidth } from "../../cssUtils/constants";
import { ReactComponent as DropdownIcon } from "../../assets/images/icons/logs/dropdown-arrow.svg";
import { loadFilters, saveFilters, FILTER_NAMESPACES } from "../../utils/filterStorage";
import { formatWithTzLabel } from "../../utils/dateUtils";
import {
  VOICE_CALL_LOGS_ENDPOINT,
  VOICE_CALL_LOGS_EXPORT_ENDPOINT,
  VOICE_CALL_LOGS_TEMPLATES_ENDPOINT,
} from "../../config/urls";
import {
  STATUS_OPTIONS,
  AGENT_OPTIONS,
  getStatusLabel,
  getAgentLabel,
  getStatusChipSx,
  formatDuration,
} from "./voiceLogConstants";
import CallDetailDrawer from "./CallDetailDrawer";

const DATE_FORMAT = "MMMM D, YYYY, h:mm A";

// Column ids double as the `ordering` field names accepted by the API.
const tableColumns = [
  {
    id: "caller",
    label: "From",
    minWidth: 150,
    sortable: true,
    render: (call) => (
      <Typography
        component="span"
        sx={{ color: ColorsConstants.secondaryBackgroundColor, fontSize: "inherit" }}
      >
        {call.caller || "-"}
      </Typography>
    ),
  },
  {
    id: "response_mode",
    label: "Agent",
    minWidth: 120,
    sortable: true,
    render: (call) => getAgentLabel(call.response_mode),
  },
  { id: "waba_number", label: "To", minWidth: 150, sortable: true },
  {
    id: "started_at",
    label: "Log Date",
    minWidth: 210,
    sortable: true,
    render: (call) => (call.started_at ? formatWithTzLabel(call.started_at, DATE_FORMAT) : "-"),
  },
  {
    id: "status",
    label: "Status",
    minWidth: 120,
    sortable: true,
    render: (call) => (
      <Chip
        label={getStatusLabel(call.status)}
        size="small"
        sx={{ fontWeight: 500, ...getStatusChipSx(call.status) }}
      />
    ),
  },
  {
    id: "duration_seconds",
    label: "Duration",
    minWidth: 100,
    sortable: true,
    render: (call) => formatDuration(call.duration_seconds),
  },
  {
    id: "hangup_cause",
    label: "Ended Because",
    minWidth: 150,
    sortable: true,
    render: (call) => call.hangup_cause || "-",
  },
  {
    id: "missed_call_template_name",
    label: "Template Name",
    minWidth: 220,
    sortable: true,
    render: (call) => call.missed_call_template_name || "-",
  },
  {
    id: "has_recording",
    label: "Recording",
    minWidth: 110,
    render: (call) => (call.has_recording ? "Available" : "-"),
  },
];

const EXPORT_LABELS = {
  default: "Export Data",
  loading: "Exporting...",
  success: "Exported",
  failed: "Export Failed",
};

// Same look as the shared ExportButton, which can't be disabled by its caller.
function VoiceExportButton({ state, setState, onExport, disabled }) {
  useEffect(() => {
    if (state !== "success" && state !== "failed") return undefined;
    const timer = setTimeout(() => setState("default"), 3000);
    return () => clearTimeout(timer);
  }, [state, setState]);

  const startIcon = {
    default: <Download />,
    loading: <CircularProgress size={20} />,
    success: <CheckCircle color="success" />,
    failed: <Cancel color="error" />,
  }[state];

  return (
    <Tooltip title={disabled ? "No calls to export" : ""} arrow>
      {/* span lets the tooltip show on a disabled button */}
      <span>
        <ButtonOne
          disabled={disabled || state === "loading" || state === "success"}
          startIcon={startIcon}
          onClick={() => state === "default" && onExport()}
          sx={{
            borderRadius: "8px",
            height: "40px",
            width: "100%",
            textTransform: "capitalize",
            color: "white",
            backgroundColor: ColorsConstants.primaryPurple110,
            "&:hover": { backgroundColor: ColorsConstants.primaryPurple110 },
            "&.Mui-disabled": {
              backgroundColor: ColorsConstants.primaryGray10,
              color: ColorsConstants.primaryGray50,
            },
          }}
        >
          {EXPORT_LABELS[state]}
        </ButtonOne>
      </span>
    </Tooltip>
  );
}

export default function VoiceLogsTable({ refreshKey, controller, setController, setLastSynced }) {
  const [rowCount, setRowCount] = useState(0);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);

  const [savedFilters] = useState(() => {
    const s = loadFilters(FILTER_NAMESPACES.VOICE_LOGS, {
      searchQuery: "",
      statusFilter: "",
      agentFilter: "",
      templateFilter: "",
      fromDate: null,
      toDate: null,
    });
    return {
      ...s,
      fromDate: s.fromDate ? dayjs(s.fromDate) : null,
      toDate: s.toDate ? dayjs(s.toDate) : null,
    };
  });
  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery);
  const [charCount, setCharCount] = useState(savedFilters.searchQuery.length);
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter);
  const [agentFilter, setAgentFilter] = useState(savedFilters.agentFilter);
  const [templateFilter, setTemplateFilter] = useState(savedFilters.templateFilter);
  const [fromDate, setFromDate] = useState(savedFilters.fromDate);
  const [toDate, setToDate] = useState(savedFilters.toDate);
  const [appliedDateRange, setAppliedDateRange] = useState([
    savedFilters.fromDate,
    savedFilters.toDate,
  ]);
  const [exportState, setExportState] = useState("default");
  const [sortState, setSortState] = useState([]);

  const { getValidToken } = useAuth();
  const latestRequest = useRef(0);


  useEffect(() => {
    saveFilters(FILTER_NAMESPACES.VOICE_LOGS, {
      searchQuery,
      statusFilter,
      agentFilter,
      templateFilter,
      fromDate: fromDate ? dayjs(fromDate).toISOString() : null,
      toDate: toDate ? dayjs(toDate).toISOString() : null,
    });
  }, [searchQuery, statusFilter, agentFilter, templateFilter, fromDate, toDate]);

  const buildFilterParams = () => {
    const params = new URLSearchParams();
    // Sent as typed: the shared phone helpers keep only the last 10 digits, which
    // would break full numbers and numeric call IDs. The API decides what it is.
    if (searchQuery.trim()) params.append("search", searchQuery.trim());
    if (statusFilter) params.append("status", statusFilter);
    if (agentFilter) params.append("response_mode", agentFilter);
    if (templateFilter) params.append("template", templateFilter);
    if (fromDate) params.append("from", dayjs(fromDate).format("YYYY-MM-DD"));
    if (toDate) params.append("to", dayjs(toDate).format("YYYY-MM-DD"));
    if (sortState.length) {
      const { id, direction } = sortState[0];
      params.append("ordering", direction === "desc" ? `-${id}` : id);
    }
    return params;
  };

  const getHeaders = async () => ({
    Authorization: `Bearer ${await getValidToken()}`,
    "Content-Type": "application/json",
    "X-Tenant-Id": localStorage.getItem("tenant"),
  });

  const fetchData = async () => {
    const requestId = ++latestRequest.current;
    const params = buildFilterParams();
    params.append("limit", controller.rowsPerPage.toString());
    params.append("offset", (controller.serverSidePage * controller.rowsPerPage).toString());

    setLoading(true);
    try {
      const response = await fetch(`${VOICE_CALL_LOGS_ENDPOINT}?${params}`, {
        headers: await getHeaders(),
      });
      if (!response.ok) throw new Error("Failed to fetch voice logs");
      const json = await response.json();
      if (requestId !== latestRequest.current) return;
      setCalls(json.results || []);
      setRowCount(json.count || 0);
      setFetchFailed(false);
      setLastSynced(
        json.last_call_at ? formatWithTzLabel(json.last_call_at, "MMMM D, YYYY, h:mm:ss A") : null,
      );
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      setCalls([]);
      setRowCount(0);
      setFetchFailed(true);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  const fetchTemplateOptions = async () => {
    try {
      const response = await fetch(VOICE_CALL_LOGS_TEMPLATES_ENDPOINT, {
        headers: await getHeaders(),
      });
      if (!response.ok) return;
      const json = await response.json();
      const rows = Array.isArray(json) ? json : json.results || [];
      setTemplateOptions(
        rows.map(({ missed_call_template_name: name }) => ({ label: name, value: name })),
      );
    } catch (error) {
      setTemplateOptions([]);
    }
  };

  useEffect(() => {
    fetchTemplateOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    controller.serverSidePage,
    controller.rowsPerPage,
    refreshKey,
    searchQuery,
    statusFilter,
    agentFilter,
    templateFilter,
    fromDate,
    toDate,
    sortState,
  ]);

  const handleExport = async () => {
    try {
      setExportState("loading");
      const response = await fetch(`${VOICE_CALL_LOGS_EXPORT_ENDPOINT}?${buildFilterParams()}`, {
        headers: await getHeaders(),
      });
      if (!response.ok) throw new Error("Failed to export voice logs");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `voice-logs-${dayjs().format("YYYY-MM-DD")}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setExportState("success");
    } catch (error) {
      setExportState("failed");
    }
  };

  const resetToFirstPage = () =>
    setController({ page: 0, rowsPerPage: controller.rowsPerPage, serverSidePage: 0 });

  const handleFilterSelect = (current, setter) => (value) => {
    setter(current === value ? "" : value);
    resetToFirstPage();
  };

  const handleDateRangeApply = () => {
    setFromDate(appliedDateRange[0]);
    setToDate(appliedDateRange[1]);
    resetToFirstPage();
  };

  const handleDateRangeCancel = () => setAppliedDateRange([fromDate, toDate]);

  const clearDates = () => {
    setFromDate(null);
    setToDate(null);
    setAppliedDateRange([null, null]);
    resetToFirstPage();
  };

  const updateSearchValue = (value) => {
    if (value.length > 50) return;
    setSearchQuery(value);
    setCharCount(value.length);
    resetToFirstPage();
  };

  const handlePageChange = (event, newPage) =>
    setController((prev) => ({ ...prev, page: newPage, serverSidePage: newPage }));

  const handleChangeRowsPerPage = (event) =>
    setController((prev) => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
      serverSidePage: 0,
    }));

  const handleResetButtonClick = () => {
    setSearchQuery("");
    setCharCount(0);
    setStatusFilter("");
    setAgentFilter("");
    setTemplateFilter("");
    setFromDate(null);
    setToDate(null);
    setAppliedDateRange([null, null]);
    setSortState([]);
    resetToFirstPage();
  };

  // One sort column at a time, cycling asc -> desc -> default (newest first).
  const handleSortChange = (columnId) => {
    setSortState((prev) => {
      const existing = prev.find((s) => s.id === columnId);
      if (!existing) return [{ id: columnId, direction: "asc" }];
      if (existing.direction === "asc") return [{ id: columnId, direction: "desc" }];
      return [];
    });
    resetToFirstPage();
  };

  const hasActiveFilters = Boolean(
    searchQuery.trim() || statusFilter || agentFilter || templateFilter || fromDate || toDate,
  );
  const emptyMessage = fetchFailed
    ? "Could not load voice logs"
    : hasActiveFilters
      ? "No calls match these filters"
      : "No call logs yet";

  const filterChip = (label, onDelete) => (
    <Chip label={label} size="small" onDelete={onDelete} sx={{ ml: 1 }} />
  );

  return (
    <Paper
      sx={{ width: "100%", overflow: "hidden", backgroundColor: "transparent", border: "none", boxShadow: "none" }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", p: 0, m: 1, borderRadius: 1 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", pb: 1 }}>
          <Box sx={{ width: searchBarWidth }}>
            <TableSearchInput
              searchQuery={searchQuery}
              onSearchChange={(e) => updateSearchValue(e.target.value)}
              charCount={charCount}
              tooltip="Search by phone number, call ID or template"
              placeholder="Search by number, call ID or template"
            />
          </Box>

          <AppDropdown
            endIcon={<DropdownIcon />}
            sx={{ whiteSpace: "nowrap" }}
            paperProps={{ maxWidth: "500px" }}
            menuContent={(closeMenu) => (
              <Box sx={{ p: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="subtitle2">Select Log Date:</Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <DatePicker
                        label="From"
                        value={appliedDateRange[0]}
                        onChange={(val) => setAppliedDateRange([val, appliedDateRange[1]])}
                        slotProps={{ textField: { size: "small", sx: { width: "200px" } } }}
                      />
                      <DatePicker
                        label="To"
                        value={appliedDateRange[1]}
                        onChange={(val) => setAppliedDateRange([appliedDateRange[0], val])}
                        slotProps={{ textField: { size: "small", sx: { width: "200px" } } }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <ButtonTwo
                        onClick={() => {
                          handleDateRangeCancel();
                          closeMenu();
                        }}
                        variant="outlined"
                        title="CANCEL"
                      />
                      <ButtonOne
                        onClick={() => {
                          handleDateRangeApply();
                          closeMenu();
                        }}
                        variant="contained"
                        title="Apply"
                      />
                    </Box>
                  </Box>
                </LocalizationProvider>
              </Box>
            )}
          >
            Log Date
            {(fromDate || toDate) &&
              filterChip(
                `${fromDate ? dayjs(fromDate).format("MM/DD/YY") : "…"} - ${
                  toDate ? dayjs(toDate).format("MM/DD/YY") : "…"
                }`,
                clearDates,
              )}
          </AppDropdown>

          <AppDropdown
            endIcon={<DropdownIcon />}
            menuItems={STATUS_OPTIONS}
            selectedValue={statusFilter}
            onMenuSelect={handleFilterSelect(statusFilter, setStatusFilter)}
          >
            Status
            {statusFilter &&
              filterChip(getStatusLabel(statusFilter), () => {
                setStatusFilter("");
                resetToFirstPage();
              })}
          </AppDropdown>

          <AppDropdown
            endIcon={<DropdownIcon />}
            menuItems={AGENT_OPTIONS}
            selectedValue={agentFilter}
            onMenuSelect={handleFilterSelect(agentFilter, setAgentFilter)}
          >
            Agent
            {agentFilter &&
              filterChip(getAgentLabel(agentFilter), () => {
                setAgentFilter("");
                resetToFirstPage();
              })}
          </AppDropdown>

          <AppDropdown
            endIcon={<DropdownIcon />}
            menuItems={templateOptions}
            selectedValue={templateFilter}
            onMenuSelect={handleFilterSelect(templateFilter, setTemplateFilter)}
            emptyText="No templates sent yet"
            searchable
            searchPlaceholder="Search templates"
          >
            Template Name
            {templateFilter &&
              filterChip(templateFilter, () => {
                setTemplateFilter("");
                resetToFirstPage();
              })}
          </AppDropdown>

          <ButtonTwo
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleResetButtonClick}
            sx={{
              height: "40px",
              borderRadius: "8px",
              border: `1px solid ${ColorsConstants.primaryBackgroundColor}`,
              color: ColorsConstants.primaryBackgroundColor,
            }}
          >
            Reset
          </ButtonTwo>

          <Box>
            <VoiceExportButton
              state={exportState}
              setState={setExportState}
              onExport={handleExport}
              disabled={loading || rowCount === 0}
            />
          </Box>
        </Box>
      </Box>

      <AppTable
        columns={tableColumns}
        rows={calls}
        rowKey={(call, index) => call.id || index}
        loading={loading}
        emptyMessage={emptyMessage}
        maxHeight={500}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sortState={sortState}
        onSortChange={handleSortChange}
        onRowClick={(call) => setSelectedCall(call)}
        rowSx={(call) =>
          selectedCall?.id === call.id ? { backgroundColor: ColorsConstants.primaryBlue60 } : {}
        }
      />

      <CallDetailDrawer call={selectedCall} onClose={() => setSelectedCall(null)} />
    </Paper>
  );
}
